import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { auditLogsApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { TableSkeleton } from '../components/TableSkeleton';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatDateTime } from '../utils/format';
import { useAuth } from '../app/AuthProvider';



export function AuditLogsPage() {
  const { user: currentUser } = useAuth();
  const showOrganization = currentUser?.role === 'SUPER_ADMIN';
  const columnCount = showOrganization ? 6 : 5;
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const debouncedSearch = useDebouncedValue(search);

  const { data: filters } = useQuery({
    queryKey: ['audit-log-filters'],
    queryFn: auditLogsApi.filters,
    staleTime: 5 * 60_000,
  });

  const queryParams = {
    page: page + 1,
    pageSize,
    search: debouncedSearch || undefined,
    module: moduleFilter || undefined,
    userId: userFilter ? Number(userFilter) : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['audit-logs', queryParams],
    queryFn: () => auditLogsApi.list(queryParams),
    placeholderData: keepPreviousData,
  });

  const resetPage = (): void => setPage(0);

  return (
    <>
      <PageHeader
        title="Audit Logs"
        description="Permanent, read-only record of every significant business action in the CRM."
      />

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Audit logs could not be loaded.'}
        </Alert>
      ) : null}

      <Card>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          sx={{ p: 2 }}
          alignItems={{ lg: 'center' }}
        >
          <TextField
            placeholder="Search descriptions…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              resetPage();
            }}
            sx={{ maxWidth: { lg: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            label="Module"
            value={moduleFilter}
            onChange={(event) => {
              setModuleFilter(event.target.value);
              resetPage();
            }}
            sx={{ maxWidth: { lg: 190 } }}
          >
            <MenuItem value="">All modules</MenuItem>
            {filters?.modules.map((module) => (
              <MenuItem key={module} value={module}>
                {module}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="User"
            value={userFilter}
            onChange={(event) => {
              setUserFilter(event.target.value);
              resetPage();
            }}
            sx={{ maxWidth: { lg: 190 } }}
          >
            <MenuItem value="">All users</MenuItem>
            {filters?.users.map((user) => (
              <MenuItem key={user.id} value={String(user.id)}>
                {user.username}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="From"
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              resetPage();
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: { lg: 170 } }}
          />
          <TextField
            label="To"
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              resetPage();
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: { lg: 170 } }}
          />
        </Stack>

        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                {showOrganization ? <TableCell>Organization</TableCell> : null}
                <TableCell>User</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableSkeleton columns={columnCount} />
              ) : data && data.items.length > 0 ? (
                data.items.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(log.createdAt)}
                    </TableCell>
                    {showOrganization ? <TableCell>{log.organizationName}</TableCell> : null}
                    <TableCell>
                      {log.user?.username ?? 'System'}
                      {log.ipAddress ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {log.ipAddress}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{log.module}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.description ?? '—'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ border: 0 }}>
                    <EmptyState
                      message="No audit entries found."
                      hint="Entries appear as users sign in and work in the system."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data?.meta.total ?? 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Card>
    </>
  );
}
